import React, { useState, useMemo } from "react";
import { Button } from "@shared/ui/button";
import { Badge } from "@shared/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Tabs } from "@shared/ui/tabs";
import { GenericTable } from "@shared/components/GenericTable";

import {
  Eye,
  X,
  Home,
  ArrowLeft,
  Clock,
  TrendingUp,
  AlertTriangle,
  Activity,
  CheckSquare,
  Package,
  Wrench,
  HelpCircle,
  Edit,
  Download,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useJobCardDetail } from "../../../hooks"; // Your existing hooks file
import { wxUtils } from "../../../../utils"; // Your existing utils file
import { useCurrency } from "@shared/selectors/globalSelectors";

const ConsumerServiceOrderView = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const currency = useCurrency()
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const getAddressStringForConsumer = (
    addressMap: any,
    isDirect: boolean = false
  ): string => {
    if (isDirect) {
      return `${addressMap?.premise || ""}, ${addressMap?.area || ""}, ${
        addressMap?.state || ""
      }`;
    }
    return `${addressMap?.premise || ""}, ${
      addressMap?.area || ""
    }, ${addressMap?.state || ""}`;
  };

  // Fetch job card data using your existing pattern
  const {
    data: jobCardResponse,
    error,
    refetch,
  } = useJobCardDetail({
    id: id,
  });

  // Transform data using your utility function
  const transformedData = useMemo(() => {
    if (!jobCardResponse) return null;
    return wxUtils.transformJobCardDetail(jobCardResponse);
  }, [jobCardResponse]);

  // Extract transformed data
  const {
    serviceOrderData,
    consumerDetails,
    technicianDetails,
    jobOverview,
    tasksData,
    materialsData,
    readingsData,
    helpResourcesData,
  } = transformedData || {};

  // KPI data derived from service order data
  const kpiData = useMemo(() => {
    if (!serviceOrderData) {
      return {
        slaStatus: "N/A",
        slaDueDate: "N/A",
        timeRemaining: "N/A",
      };
    }

    return {
      slaStatus: serviceOrderData.slaStatus,
      slaDueDate: serviceOrderData.expectedCompletion,
      timeRemaining: serviceOrderData.remainingTime,
    };
  }, [serviceOrderData]);

  const getPriorityColor = (priority: string) => {
    switch (priority?.toLowerCase()) {
      case "critical":
        return "bg-red-100 text-red-800 border-red-200";
      case "high":
        return "bg-orange-100 text-orange-800 border-orange-200";
      case "medium":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "low":
        return "bg-green-100 text-green-800 border-green-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  // Table column configurations
  const tasksColumns = [
    {
      key: "taskName",
      header: "Task Name",
      render: (value: string) => (
        <span className="font-medium text-gray-900">{value}</span>
      ),
    },
    {
      key: "tatMins",
      header: "TAT (Mins)",
      render: (value: number) => <span className="text-gray-700">{value}</span>,
    },
    {
      key: "downtimeMins",
      header: "Downtime (Mins)",
      render: (value: number) => (
        <span className={value > 0 ? "text-orange-600" : "text-gray-700"}>
          {value}
        </span>
      ),
    },
    {
      key: "photoRequired",
      header: "Photo Required",
      type: "badge" as const,
      badgeConfig: {
        getClassName: (value: string) =>
          value === "Yes"
            ? "bg-green-100 text-green-800"
            : "bg-gray-100 text-gray-800",
      },
    },
  ];

  const materialsColumns = [
    {
      key: "name",
      header: "Name",
      render: (value: string) => (
        <span className="font-medium text-gray-900">{value}</span>
      ),
    },
    {
      key: "size",
      header: "Size",
      render: (value: string) => <span className="text-gray-700">{value}</span>,
    },
    {
      key: "quantity",
      header: "Quantity",
      render: (value: number) => <span className="text-gray-700">{value}</span>,
    },
    {
      key: "cost",
      header: "Cost",
      render: (value: number) => (
        <span className="font-semibold text-green-600">
          {currency} {value?.toFixed(2) || "0.00"}
        </span>
      ),
    },
  ];

  const readingsColumns = [
    {
      key: "name",
      header: "Name",
      render: (value: string) => (
        <span className="font-medium text-gray-900">{value}</span>
      ),
    },
    {
      key: "unit",
      header: "Unit",
      render: (value: string) => <span className="text-gray-700">{value}</span>,
    },
    {
      key: "range",
      header: "Range",
      render: (value: string) => <span className="text-gray-700">{value}</span>,
    },
    {
      key: "photoRequired",
      header: "Photo Required",
      type: "badge" as const,
      badgeConfig: {
        getClassName: (value: string) =>
          value === "Yes"
            ? "bg-green-100 text-green-800"
            : "bg-gray-100 text-gray-800",
      },
    },
  ];

  const helpResourcesColumns = [
    {
      key: "resourceName",
      header: "Resource Name",
      render: (value: string) => (
        <span className="font-medium text-blue-600 hover:text-blue-800 cursor-pointer">
          {value}
        </span>
      ),
    },
    {
      key: "resourceType",
      header: "Type",
      type: "badge" as const,
      badgeConfig: {
        getClassName: (value: string) => {
          switch (value) {
            case "Document":
            case "instruction":
              return "bg-blue-100 text-blue-800";
            case "Video Link":
              return "bg-red-100 text-red-800";
            case "Checklist":
              return "bg-green-100 text-green-800";
            case "Hotline":
              return "bg-purple-100 text-purple-800";
            default:
              return "bg-gray-100 text-gray-800";
          }
        },
      },
    },
    {
      key: "details",
      header: "Details",
      render: (value: string) => <span className="text-gray-700">{value}</span>,
    },
  ];

  // Table actions

  // Pagination config
  const createPaginationConfig = (data: any[]) => ({
    currentPage,
    totalPages: Math.ceil(data.length / itemsPerPage),
    totalItems: data.length,
    itemsPerPage,
    startItem: (currentPage - 1) * itemsPerPage + 1,
    endItem: Math.min(currentPage * itemsPerPage, data.length),
  });

  // Get paginated data
  const getPaginatedData = (data: any[]) => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return data.slice(startIndex, startIndex + itemsPerPage);
  };

  // Create tab components using the URL Tabs pattern
  const tabComponents = useMemo(
    () => ({
      overview: {
        label: "Overview",
        icon: <Activity className="h-4 w-4" />,
        component: (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="text-sm font-medium text-gray-500">
                Description
              </label>
              <p className="text-base text-gray-900 mt-1">
                {serviceOrderData?.description || "N/A"}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">
                Required Field Force
              </label>
              <p className="text-base text-gray-900 mt-1">
                {jobOverview?.requiredFieldforce || "N/A"}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">
                Estimated Efforts
              </label>
              <p className="text-base text-gray-900 mt-1">
                {jobOverview?.efforts || "N/A"}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">
                Downtime
              </label>
              <p className="text-base text-gray-900 mt-1">
                {jobOverview?.downtime || "N/A"}
              </p>
            </div>
          </div>
        ),
      },
      tasks: {
        label: "Tasks",
        icon: <CheckSquare className="h-4 w-4" />,
        count: tasksData?.length || 0,
        component: (
          <GenericTable
            data={getPaginatedData(tasksData || [])}
            columns={tasksColumns}
            rowKey="taskId"
            actionsType="dropdown"
            pagination={createPaginationConfig(tasksData || [])}
            onPageChange={setCurrentPage}
            emptyMessage="No tasks found"
          />
        ),
      },
      materials: {
        label: "Materials",
        icon: <Package className="h-4 w-4" />,
        count: materialsData?.length || 0,
        component: (
          <GenericTable
            data={getPaginatedData(materialsData || [])}
            columns={materialsColumns}
            rowKey="materialId"
            actionsType="dropdown"
            pagination={createPaginationConfig(materialsData || [])}
            onPageChange={setCurrentPage}
            emptyMessage="No materials found"
          />
        ),
      },
      readings: {
        label: "Readings",
        icon: <Wrench className="h-4 w-4" />,
        count: readingsData?.length || 0,
        component: (
          <GenericTable
            data={getPaginatedData(readingsData || [])}
            columns={readingsColumns}
            rowKey="readingId"
            actionsType="dropdown"
            pagination={createPaginationConfig(readingsData || [])}
            onPageChange={setCurrentPage}
            emptyMessage="No readings found"
          />
        ),
      },
      help: {
        label: "Help Resources",
        icon: <HelpCircle className="h-4 w-4" />,
        shortLabel: "Help",
        count: helpResourcesData?.length || 0,
        component: (
          <GenericTable
            data={getPaginatedData(helpResourcesData || [])}
            columns={helpResourcesColumns}
            rowKey="resourceId"
            actionsType="dropdown"
            pagination={createPaginationConfig(helpResourcesData || [])}
            onPageChange={setCurrentPage}
            emptyMessage="No help resources found"
          />
        ),
      },
    }),
    [
      currentPage,
      serviceOrderData,
      jobOverview,
      tasksData,
      materialsData,
      readingsData,
      helpResourcesData,
    ]
  );

  // No data state
  if (!transformedData || !serviceOrderData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center"></div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Header Bar */}
      <div className="bg-white border-b border-gray-200 shadow-sm"></div>

      {/* Header Breadcrumb */}
      <div className="bg-white border-b border-gray-100"></div>

      {/* Page Header */}
      <div className="bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  Service Order - {serviceOrderData.soName}
                </h1>
                <p className="text-lg text-gray-600 mt-1">
                  {serviceOrderData.priority} priority consumer service
                  association management
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={() =>
                  navigate("/service-orders/dispatcher?page=1&tab=pending")
                }
                className="flex items-center gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Dispatcher
              </Button>
              <Button
                variant="ghost"
                onClick={() =>
                  navigate("/service-orders/dispatcher?page=1&tab=pending")
                }
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="mx-auto px-6 py-8 space-y-8 bg-white">
        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="border-l-4 border-l-green-500 hover:shadow-lg transition-shadow ">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-gray-700">
                SLA Status
              </CardTitle>
              <TrendingUp className="h-5 w-5 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold ">{kpiData.slaStatus}</div>
              <p className="text-xs text-gray-500 mt-1">
                Within acceptable limits
              </p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-blue-500 hover:shadow-lg transition-shadow ">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-gray-700">
                SLA Due Date
              </CardTitle>
              <Clock className="h-5 w-5 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold ">{kpiData.slaDueDate}</div>
              <p className="text-xs text-gray-500 mt-1">Meeting timeline</p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-orange-500 hover:shadow-lg transition-shadow ">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-gray-700">
                Time Remaining
              </CardTitle>
              <AlertTriangle className="h-5 w-5 text-orange-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{kpiData.timeRemaining}</div>
              <p className="text-xs text-gray-500 mt-1">On schedule</p>
            </CardContent>
          </Card>
        </div>

        {/* Service Order and Consumer Details Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Service Order Details Card - Blue Theme */}
          <Card className="bg-white shadow-sm border border-gray-100 rounded-lg hover:shadow-md transition-shadow border-l-4 border-l-blue-500">
            <CardHeader className="bg-gradient-to-r from-slate-50 to-gray-50 border-b p-2">
              <CardTitle className="text-xl font-semibold">
                Service Order Details
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      SO ID
                    </label>
                    <p className="text-base text-gray-900">
                      {serviceOrderData.jobCardNo}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Status
                    </label>
                    <p className="text-base text-gray-900">
                      {serviceOrderData.status}
                    </p>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    SO Name
                  </label>
                  <p className="text-base text-gray-900">
                    {serviceOrderData.soName}
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Priority
                    </label>
                    <br />
                    <Badge
                      className={`${getPriorityColor(
                        serviceOrderData.priority
                      )} px-2 py-1 text-xs font-medium border`}
                    >
                      <span className="w-2 h-2 rounded-full bg-current mr-1"></span>
                      {serviceOrderData.priority}
                    </Badge>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Created On
                    </label>
                    <p className="text-base text-gray-900">
                      {serviceOrderData.createdOn}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Area
                    </label>
                    <p className="text-base text-gray-900">
                      {serviceOrderData.area || "N/A"}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Sub Area
                    </label>
                    <p className="text-base text-gray-900">
                      {serviceOrderData.subarea || "N/A"}
                    </p>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Service Association
                  </label>
                  <p className="text-base text-gray-900">
                    {serviceOrderData.utilityServices || "Consumer"}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Consumer Details Card - Yellow/Orange Theme */}
          <Card className="bg-white shadow-sm border border-gray-100 rounded-lg hover:shadow-md transition-shadow border-l-4 border-l-orange-500">
            <CardHeader className="bg-gradient-to-r from-slate-50 to-gray-50 border-b p-2">
              <CardTitle className="text-xl font-semibold">
                Consumer Details
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Account No
                    </label>
                    <p className="text-base text-gray-900">
                      {consumerDetails?.accountNo || "N/A"}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Phone No
                    </label>
                    <p className="text-base text-gray-900">
                      {consumerDetails?.phone || "N/A"}
                    </p>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Account Name
                  </label>
                  <p className="text-base text-gray-900">
                    {consumerDetails?.consumerName || "N/A"}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Utility Services
                  </label>
                  <p className="text-base text-gray-900">
                    {serviceOrderData?.utilityServices || "N/A"}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Address
                  </label>
                  <p className="text-base text-gray-900">
                    {getAddressStringForConsumer(consumerDetails) || "N/A"}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Job Requirements Card with Advanced Tabs */}
        <Card className="bg-white shadow-sm border border-gray-100 rounded-lg hover:shadow-md transition-shadow">
          <CardHeader className="bg-gradient-to-r from-slate-50 to-gray-50 border-b p-2">
            <CardTitle className="text-xl font-semibold text-gray-800">
              Job Requirements
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <Tabs
              defaultValue="overview"
              idPrefix="consumer-job-requirements"
              tabComponents={tabComponents}
              tabsListClassName="grid w-full grid-cols-5"
              disableUrlSync={true}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ConsumerServiceOrderView;
